import React, { useCallback, useEffect, useReducer, useState } from 'react'
import { useMaasAccount } from '@maas-tools/accounts'
import { useMaasKeycloak } from '@maas-tools/keycloak'
import WidgetsOutlinedIcon from '@material-ui/icons/WidgetsOutlined'
import { Header, Menu, Sidebar } from '@shell-components/core'
import axios from 'axios'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import * as Analytics from 'core/common/analytics'
import * as Ability from 'core/common/constants/ability'
import { KEYCLOAK_REDIRECT_URL_LOGOUT, SHELL_PUBLIC_URL } from 'core/common/constants/env'
import { MARKETPLACE_INTEGRA_URL, SHELL_LISTENER_LOGOUT_KEY } from 'core/common/constants/settings'
import useSellerPermissions from 'core/common/hooks/useSellerPermissions'
import i18n from 'core/common/i18n'
import pkg from '../../../../package.json'

/**
 * Filtro de serviços que o seller tem permissão de acesso.
 */
const filterServices = (services, currentSeller, currentService) => {
  if (!services?.length && !currentSeller?.services?.length) {
    return []
  }

  return services
    .filter(service => currentSeller.services.some(current => current.id === service.service_id))
    .map(service => ({
      ...(service.service_id === currentService
        ? { ...service, url: '/' }
        : { ...service, external: true }),
    }))
}

/**
 * Retorna o componente de sidebar com menu já populado.
 */
const SidebarMenu = ({ children, ...rest }) => {
  const { hasPermissions } = useSellerPermissions()

  const canAccessOptimizeRegister = hasPermissions(Ability.SERVICE_MARKET_MATCHER)

  const links = [
    { name: i18n.t('menu.catalogProducts'), url: '/' },
    {
      name: i18n.t('menu.registerProduct'),
      url: '/novo-produto/escolher-tipo-de-produto',
    },
    { name: i18n.t('menu.importSpreadsheet'), url: '/import' },
    { name: i18n.t('menu.ImportHistory'), url: '/import/history' },
  ]

  if (canAccessOptimizeRegister) {
    links.push({ name: i18n.t('menu.matchProducts'), url: '/otimizar-cadastro' })
  }

  const menu = {
    title: {
      icon: <WidgetsOutlinedIcon />,
      name: i18n.t('menu.products'),
    },
    links,
  }

  const handleClickMenuItem = (_, link) => {
    Analytics.clickEventSidebarMenuItem(link.name)
  }

  return (
    <Sidebar
      menu={menu}
      version={pkg.version}
      NavLink={Link}
      {...rest}
      onClickMenuItem={handleClickMenuItem}
    >
      {children}
    </Sidebar>
  )
}

const AppShell = ({ children }) => {
  const { keycloak } = useMaasKeycloak()
  const { currentSeller, sellers } = useMaasAccount()
  const [isOpenMenu, onOpenMenu] = useReducer(bool => !bool, false)
  const [isLogout, onLogout] = useReducer(bool => !bool, false)
  const [services, setServices] = useState([])
  const [, setLoading] = useState(false)

  /**
   * Retorna os serviços que estão integrados ao portal do seller.
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`${SHELL_PUBLIC_URL}/menu.json`)
      setServices(filterServices(data ?? [], currentSeller, 'product-api'))
    } finally {
      setLoading(false)
    }
  }, [currentSeller])

  /**
   *  Função assíncrona para o ciclo de vida do componente.
   */
  useEffect(() => {
    fetchData()
  }, [fetchData])

  /**
   * Condicional caso a ação de logout for verdadeira.
   * Remove o tenantId para não conflitar com a próxima sessão e redireciona
   * para a URl de logout do keycloak.
   */
  if (isLogout && window && sessionStorage) {
    sessionStorage.removeItem('maas-tenantId')
    if (window) {
      window.postMessage(SHELL_LISTENER_LOGOUT_KEY, window.location.origin)
      window.location.replace(KEYCLOAK_REDIRECT_URL_LOGOUT)
    }
  }

  return (
    <>
      <Header
        services={services}
        email={keycloak.tokenParsed.email}
        name={keycloak.tokenParsed.name}
        currentSeller={currentSeller}
        sellers={sellers}
        onClickMenu={onOpenMenu}
        onLogout={onLogout}
        RouterLink={Link}
      />
      <Menu
        services={services}
        open={isOpenMenu}
        onClose={onOpenMenu}
        mktUrl={MARKETPLACE_INTEGRA_URL}
        RouterLink={Link}
      />
      {children}
    </>
  )
}

SidebarMenu.propTypes = {
  children: PropTypes.node.isRequired,
}

AppShell.propTypes = {
  children: PropTypes.node.isRequired,
}

export { SidebarMenu }
export default AppShell
